import React, { useEffect, useMemo } from "react";
import { Formik } from "formik";
import { IRootState } from "us.collection/interfaces";
import * as Actions from "us.collection.routines-and-activities/actions";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { INote } from "us.collection.routines-and-activities/components/Note/Interfaces";
import { getCaseNumberAndType } from "us.collection.routines-and-activities/components/Note/Functions";
import * as Azure from "us.helper/azure";
import { isEditableUserNote } from "us.collection.routines-and-activities/functions";
import { Type } from "us.collection.routines-and-activities/constants";
import {
  NoteType,
  RegisteredUserType,
  NoteSource,
} from "us.collection/constants";
import { SendDomainCPNote, UpdateNotePayload } from "./Repository";
import { SendOutlined, UserOutlined, BoxIcons, IconTypes } from "us.icons";
const { historyAction } = Actions;
const { noteStatus, note, deleteNote, cpNote } = historyAction;
const { $TextArea, $Switch, $Button, $Typography, $Popconfirm, $Avatar } =
  Common.Components;

const { Paragraph } = $Typography;

const Note: React.FC<INote & PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const {
    setNotifyStatus,
    close,
    data,
    edit,
    deleteNote,
    location,
    metaData,
    isNoteDeleting,
    saveReplyNote,
    isNoteSaving,
  } = props;
  const { noteId, notified, source, noteType } = data ?? {};

  let isAzureAD = window._ENV.REACT_APP_AZURE_AD_SETUP;
  let auth: any;
  if (isAzureAD) {
    auth = new Azure.ADAuth();
  } else {
    auth = new Azure.B2CAuth();
  }

  const azureUser: any = auth.currentUser();

  useEffect(() => {
    if (
      !notified &&
      (noteType === NoteType.CP_NOTE ? source != NoteSource.US_BOX : true)
    ) {
      setNotifyStatus && setNotifyStatus({ noteId });
    }
  }, []);

  const updateNote = (values: any) => {
    const notePayload = UpdateNotePayload.call({
      values,
      noteId,
      location,
      metaData,
    });

    edit && edit({ params: notePayload, closeAction: close });
  };

  const noteDelete = () => {
    if (metaData.data)
      deleteNote &&
        deleteNote({
          params: {
            noteId,
            entityType: getCaseNumberAndType(location, metaData.data)?.type,
          },
          closeAction: close,
        });
  };

  const sendReplyNote = (values: any) => {
    if (metaData.data) {
      const queryObject = SendDomainCPNote.call({data, location}, {
          ...metaData.data,
      }, values.replyNoteText);
      saveReplyNote && saveReplyNote({params:queryObject, closeAction: close});
    }
  };
  return (
    <Formik
      enableReinitialize
      initialValues={{
        internalNote: data?.isInternalNote,
        copyToDebNotes: data?.copiedToDebtorNotes,
        noteText: data?.name,
        isSystemGeneratedNote:
          data?.createdBy?.toLowerCase() ==
          RegisteredUserType.SYSTEM?.toLowerCase(),
        replyNoteText: "",
      }}
      onSubmit={updateNote}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        ...rest
      }: any) => (
        <>
          <div>
            {noteType == NoteType.CP_NOTE && source == NoteSource.API && (
              <div className="d-flex justify-content-start mb-2">
                <$Avatar
                  style={{
                    backgroundColor: "#1890ff",
                    fontSize: "80%",
                  }}
                  icon={
                    <BoxIcons type={IconTypes.BOX_ICON} name="creditor-notes" />
                  }
                  size="small"
                />
                <span className="ml-2">
                  {t(
                    "US.COLLECTION.ROUTINESANDACTIVITIES:HISTORY.FROM_CREDITOR_PORTAL"
                  )}
                </span>
              </div>
            )}
            <$TextArea
              name="noteText"
              className="note-message"
              style={{
                height:
                  noteType == NoteType.CP_NOTE && source == NoteSource.API
                    ? "calc(44vh - 90px)"
                    : "",
              }}
              disabled={
                !isEditableUserNote(data, azureUser?.unique_name) ||
                noteType == NoteType.CP_NOTE
              }
            />
          </div>
          {noteType == NoteType.CP_NOTE && source == NoteSource.API && (
            <div className="mt-4">
              <div className="d-flex justify-content-end mb-2">
                <div className="d-flex align-items-center">
                  <span className="mr-2">{t("US.COLLECTION.ROUTINESANDACTIVITIES:HISTORY.YOU")}</span>
                  <$Avatar
                    style={{
                      backgroundColor: "#5E54BA",
                    }}
                    icon={<UserOutlined />}
                    size="small"
                  />
                </div>
              </div>
              <$TextArea
                name="replyNoteText"
                className="note-message"
                placeholder={t(
                  "US.COLLECTION.ROUTINESANDACTIVITIES:HISTORY.REPLY_HERE"
                )}
                style={{
                  height:
                    noteType == NoteType.CP_NOTE && source == NoteSource.API
                      ? "calc(44vh - 90px)"
                      : "",
                }}
              />
            </div>
          )}
          {noteType != NoteType.CP_NOTE &&
            metaData.data &&
            getCaseNumberAndType(location, metaData.data)?.type != Type.AR &&
            getCaseNumberAndType(location, metaData.data)?.type != Type.CR && (
              <div
                className="dom-amt-list-table mb-2"
                style={{
                  tableLayout: "fixed",
                }}
              >
                <div className="list-table-row">
                  <div
                    className="list-table-col"
                    style={{
                      width: 120,
                    }}
                  >
                    <div className="d-flex align-items-center mt-3">
                      <$Switch
                        size="small"
                        name="internalNote"
                        checked={values.internalNote}
                        disabled={
                          !isEditableUserNote(data, azureUser?.unique_name)
                        }
                      />
                      <Paragraph ellipsis className="mb-0 ml-2">
                        {t("US.COLLECTION.CASE:NOTESANDMESSAGES.INTERNAL")}
                      </Paragraph>
                    </div>
                  </div>
                  <div className="list-table-col">
                    <div className="d-flex align-items-center mt-3">
                      <$Switch
                        size="small"
                        name="copyToDebNotes"
                        checked={values.copyToDebNotes}
                        disabled={
                          !isEditableUserNote(data, azureUser?.unique_name)
                        }
                      />
                      <Paragraph ellipsis className="mb-0 ml-2">
                        {t(
                          "US.COLLECTION.CASE:NOTESANDMESSAGES.COPYASDEBTORNOTES"
                        )}
                      </Paragraph>
                    </div>
                  </div>
                </div>
              </div>
            )}
          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              <$Popconfirm
                title={t("US.COLLECTION.CASE:MANAGE_AGREEMENTS.SURE_TO_DELETE")}
                okText={t("US.COMMON:COMMON.YES")}
                cancelText={t("US.COMMON:COMMON.NO")}
                onConfirm={() => noteDelete()}
                placement="right"
                disabled={
                  !isEditableUserNote(data, azureUser?.unique_name) ||
                  isNoteDeleting
                }
              >
                <$Button
                  className="mr-2"
                  loading={isNoteDeleting}
                  disabled={
                    !isEditableUserNote(data, azureUser?.unique_name) ||
                    isNoteDeleting
                  }
                  danger
                  type="primary"
                >
                  {t("US.COLLECTION.COMMON:COMMON.DELETE")}
                </$Button>
              </$Popconfirm>
              {noteType != NoteType.CP_NOTE && (
                <$Button
                  className="mr-2"
                  disabled={!isEditableUserNote(data, azureUser?.unique_name)}
                  type="primary"
                  onClick={handleSubmit}
                >
                  {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                </$Button>
              )}
              {noteType == NoteType.CP_NOTE && source == NoteSource.API && (
              <$Popconfirm
                title={t("US.COLLECTION.CASE:NOTESANDMESSAGES.ARE_YOU_SURE_YOU_WANT_TO_SEND_THE_MESSAGE")}
                okText={t("US.COMMON:COMMON.YES")}
                cancelText={t("US.COMMON:COMMON.NO")}
                onConfirm={() => sendReplyNote(values)}
                placement="right"
                disabled={values?.replyNoteText?.trim().length === 0}
              >
                <$Button
                  className="mr-2"
                  disabled={values?.replyNoteText?.trim().length === 0}
                  type="primary"
                  loading={isNoteSaving}
                  icon={<SendOutlined />}
                >
                  {t("US.COLLECTION.CASE:NOTESANDMESSAGES.REPLY")}
                </$Button>
                </$Popconfirm>
              )}
              <$Button onClick={() => close()}>
                {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
              </$Button>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: IRootState) => {
  const { common, domainView, logHistory } = state;
  const { currentLanguage, currentDateFormat } = common;
  const { metaData } = domainView;
  const { isNoteDeleting, isNoteSaving } = logHistory;
  return {
    currentLanguage,
    currentDateFormat,
    isNoteDeleting,
    metaData,
    isNoteSaving,
  };
};

const mapDispatchToProps = {
  edit: note.save,
  setNotifyStatus: noteStatus.set,
  deleteNote: deleteNote.delete,
  saveReplyNote: cpNote.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Note);
